import {
  uniqBy, toLower, trim, first, isEmpty,
} from 'lodash-es';
import i18next from '@/lib/i18next';
import { extractTextFromHtml, stripTags } from '@/lib/utils';
import chain from '@/lib/utils/chain';
import {
  TRY_TYPE_SAVE,
  TEI_ASSET_SUBTYPE_CONSTRUCTED_RESPONSE,
  TEI_ASSET_SUBTYPE_TILING,
  TEI_ASSET_SUBTYPE_SELECT_OBJECT,
} from '@/lib/constants';
import {
  TEI_DEFAULT_LANGUAGE,
} from '@/lib/constants/teiLanguageMap';

export const BLANK_OPTION_EXCEPTION_TEXT = 'Empty Fields - You have Responses that are empty.';
export const MINIMUM_OPTIONS_EXCEPTION_TEXT = 'Must have at least 2 Responses.';
export const CORRECT_ID_EXCEPTION_TEXT = 'Correct Response Missing - You need to select one or more of your responses to be the correct answer.';
export const DUPLICATE_OPTIONS_EXCEPTION_TEXT = 'Unique Answers - You have Responses that are duplicates.';
export const BLANK_PROMPT_EXCEPTION_TEXT = 'Empty Fields - The Question Prompt is empty.';
export const LONG_PROMPT_EXCEPTION_TEXT = 'The Question Prompt has too many characters';

export const LONG_PROMPT_CHAR_LIMIT = 450;

export const STUDIO_TEI_ANSWER_WRAPPER_CLASS = 'studio-tei-answer-wrapper';

/**
 * Wrap TEA (constructed response) html in a div so its class selector can be
 * targeted in core styles.
 * @param {String} html - The answer html to wrap
 * @returns {HTMLDivElement} - The wrapper element containing the html
 */
export function wrapTeiAnswerHtml(html) {
  const wrapper = document.createElement('div');
  wrapper.classList.add(STUDIO_TEI_ANSWER_WRAPPER_CLASS);
  wrapper.innerHTML = html;
  return wrapper;
}

/**
 * Resolve a Constructed Response TEI's saved response into wrapped answer html.
 * @param {Object} response - The TEI attempt item's `try.response`
 * @returns {String|null}
 */
export function mapConstructedResponseAnswer(response) {
  const html = first(response?.answerFieldData);
  if (!html) return null;

  return html.includes(STUDIO_TEI_ANSWER_WRAPPER_CLASS)
    ? html
    : wrapTeiAnswerHtml(html).outerHTML;
}

/*
  Any value other than an empty string means that we have saved user responses
  for this try. This includes the user clearing response which will save a
  'null' value or an empty object.
*/
export function hasSavedResponse(attemptItem) {
  const itemTry = attemptItem?.try;
  return itemTry?.response !== '' && itemTry?.type_id === TRY_TYPE_SAVE;
}

// A constructed-response is empty when every answer field has no text content.
export function isEmptyConstructedResponse(response) {
  const fields = response?.answerFieldData;
  if (isEmpty(fields)) return true;
  return fields.every((field) => !stripTags(field));
}

// A tiling response is empty when every drop zone's `objects` array is empty.
function isEmptyTilingResponse(response) {
  if (isEmpty(response)) return true;
  return Object.values(response).every((zone) => isEmpty(zone?.objects));
}

// A select-object response is empty when its `objects` array is empty.
function isEmptySelectObjectResponse(response) {
  if (isEmpty(response)) return true;
  return isEmpty(response.objects);
}

// The subtype may be exposed as `sub_type` or `subtype`, as an object or a raw id.
function getTeiSubTypeId(asset) {
  const subType = asset?.sub_type ?? asset?.subtype;
  return subType?.id ?? subType;
}

/*
  Determines whether an attempt item holds an actual (non-empty) user response.
  Uses lodash isEmpty for the general case, plus subtype-specific checks for
  TEIs whose "empty" state is a populated-but-blank object. `asset` is the
  cached asset for this TEI (assets[teiId] in the asset store) — the caller
  resolves it, keeping this a pure function.
*/
export function hasNonEmptyResponse(attemptItem, asset) {
  const response = attemptItem?.try?.response;
  if (isEmpty(response)) return false;

  const subTypeId = getTeiSubTypeId(asset);

  switch (subTypeId) {
    case TEI_ASSET_SUBTYPE_CONSTRUCTED_RESPONSE:
      return !isEmptyConstructedResponse(response);
    case TEI_ASSET_SUBTYPE_TILING:
      return !isEmptyTilingResponse(response);
    case TEI_ASSET_SUBTYPE_SELECT_OBJECT:
      return !isEmptySelectObjectResponse(response);
    default:
      return true;
  }
}

/**
 * Check the validity of an interactive module before saving to Pinia/sending a PATCH
 * @param moduleData - Full module data from the store
 * @returns {Boolean} - `true` if TEI is valid
 * @throws {Error} If TEI is invalid, with reason as the error message
 */
export function validateTEI(moduleData) {
  const { options } = moduleData.options;
  const isMultipleChoice = options
    && ['single_selection', 'multiple_selection'].indexOf(moduleData.type) !== -1;

  if (isMultipleChoice) {
    if (options.some((opt) => !trim(opt))) {
      throw new Error(i18next.t(BLANK_OPTION_EXCEPTION_TEXT));
    }

    if (options.length < 2) {
      throw new Error(i18next.t(MINIMUM_OPTIONS_EXCEPTION_TEXT));
    }

    if (!moduleData.options.valid_options.length) {
      throw new Error(i18next.t(CORRECT_ID_EXCEPTION_TEXT));
    }

    const duplicateFormatter = (opt) => trim(toLower(opt));
    if (uniqBy(options, duplicateFormatter).length !== options.length) {
      throw new Error(i18next.t(DUPLICATE_OPTIONS_EXCEPTION_TEXT));
    }
  }

  if (!trim(moduleData.options.content)) {
    throw new Error(i18next.t(BLANK_PROMPT_EXCEPTION_TEXT));
  }

  if (moduleData.options.content.length > LONG_PROMPT_CHAR_LIMIT) {
    throw new Error(i18next.t(LONG_PROMPT_EXCEPTION_TEXT));
  }

  return true;
}

/**
 * Return the item within a Frayer quadrant block array that should be treated
 * as the quadrant header. Prefers a `text` item whose `lang === teiLang`; falls
 * back to the first `text` item in any language. Returns `undefined` when no
 * `text` item is present.
 *
 * This is the single source of truth for "which item is the header" — both
 * getHeaderTextFromBlock (to render the header) and FrayerGraphicOrganizerModel
 * (to exclude the header item from content-block resolution) rely on it.
 * @param {Array} block - Array of content items for one Frayer quadrant
 * @param {string} teiLang - The configured TEI language code
 * @returns {Object|undefined}
 */
export function getHeaderBlock(block, teiLang) {
  if (!Array.isArray(block)) return undefined;

  // Prefer a text item whose lang matches teiLang; fall back to first text item.
  const preferred = block.find((b) => b.type === 'text' && b.lang === teiLang);
  const fallback = block.find((b) => b.type === 'text');

  return preferred || fallback;
}

/**
 * Return the authored header text from a Frayer quadrant block array.
 * Resolves the header item via getHeaderBlock() and passes its `content`
 * through extractTextFromHtml() only — skipping extractHtml() — so that inline
 * marks like <strong> and <em> are stripped but their text content is preserved.
 * (extractHtml only allows br/div/span; passing header content through it would
 * discard text wrapped in bold/italic marks and return an empty string.)
 * Returns '' when no `text` item is found — the caller falls back to the
 * localized default for that quadrant index.
 * @param {Array} block - Array of content items for one Frayer quadrant
 * @param {string} teiLang - The configured TEI language code
 * @returns {string}
 */
export function getHeaderTextFromBlock(block, teiLang) {
  const match = getHeaderBlock(block, teiLang);

  if (!match) return '';

  // Do NOT pass through extractHtml — it only allows br/div/span and would
  // silently discard text inside <strong>, <em>, etc. Call extractTextFromHtml
  // directly to strip tags while keeping their text content.
  return extractTextFromHtml(match.content);
}

/**
 * Resolve a translatable field that follows the lang-array shape used by
 * GO uiLayouts (venn circle `label`, input `placeholder_text`):
 *
 *   field: [{ lang: 'eng', content: '...' }, { lang: 'spa', content: '...' }]
 *
 * Picks the entry matching `teiLang` and returns its `content` string.
 * Falls back to the TEI_DEFAULT_LANGUAGE entry, then to the first non-empty
 * entry. Accepts legacy flat strings for backward compat (returns them as-is)
 * — old TEIs that predate the shape change render unchanged until the edde
 * backfill runs.
 *
 * @param {Array|string|undefined} value
 * @param {string} teiLang
 * @returns {string}
 */
export function resolveLangArrayField(value, teiLang) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return '';
  const preferred = value.find((v) => v && v.lang === teiLang && typeof v.content === 'string');
  const fallback = value.find((v) => v && v.lang === TEI_DEFAULT_LANGUAGE && typeof v.content === 'string');
  const anyEntry = value.find((v) => v && typeof v.content === 'string');
  return (preferred || fallback || anyEntry)?.content || '';
}

/**
 * Filter a lang-annotated content array (Table cell.content, TChart
 * column.content, Venn circle.content, BubbleMap block.content, etc.)
 * down to the items appropriate for the active TEI language.
 *
 * Preserves items with no `lang` field (input, asset, and any legacy
 * pre-translation text). For items with a `lang` field, prefers
 * `teiLang`, falls back to TEI_DEFAULT_LANGUAGE, and then to any
 * remaining language (last-resort). Non-`text` items always pass
 * through — inputs and assets are language-agnostic and share the
 * cell with the filtered text.
 *
 * Written for NGSSNATL-2062: the translation pipeline at
 * titan-ai/server/utils/translation/teiTranslator.ts writes eng+spa
 * text siblings into every cell; without this filter, both languages
 * render stacked.
 *
 * @param {Array} items - Content items from a GO TEI cell/section/block.
 * @param {string} teiLang - The configured TEI language code.
 * @returns {Array} A filtered copy, order preserved. Returns `[]` when
 *   input is nullish or not an array.
 */
export function pickLangItems(items, teiLang) {
  if (!Array.isArray(items)) return [];

  const nonText = items.filter((i) => i && i.type !== 'text');
  const textItems = items.filter((i) => i && i.type === 'text');
  const preferred = textItems.filter((i) => i.lang === teiLang || !i.lang);
  const fallback = textItems.filter((i) => i.lang === TEI_DEFAULT_LANGUAGE || !i.lang);
  // eslint-disable-next-line no-nested-ternary
  const chosen = preferred.length ? preferred : (fallback.length ? fallback : textItems);

  const keepSet = new Set([...chosen, ...nonText]);
  return items.filter((i) => keepSet.has(i));
}

/**
 * Derive T-Chart columns from a TEI's uiLayout. Prefers the table-style `rows`
 * shape: row 0 is headers, row 1 is the body row, later rows are ignored (some
 * manuscripts emit extras, which would stack inputs). Falls back to `columns`.
 * @param {Object} uiLayout
 * @returns {Array} Per-column `{ header_content, main_content }`.
 */
export function deriveTChartColumns(uiLayout) {
  const ui = uiLayout || {};
  const rows = Array.isArray(ui.rows) ? ui.rows : [];
  if (rows.length) {
    const nCols = Math.max(...rows.map((r) => (r?.cells?.length || 0)));
    const headerRow = rows[0];
    const bodyRow = rows[1];
    const out = [];
    for (let ci = 0; ci < nCols; ci += 1) {
      const headerCell = headerRow?.cells?.[ci];
      const bodyCell = bodyRow?.cells?.[ci];
      const header = Array.isArray(headerCell?.content) ? headerCell.content : [];
      const main = Array.isArray(bodyCell?.content) ? bodyCell.content : [];
      out.push({ header_content: header, main_content: main });
    }
    return out;
  }
  return Array.isArray(ui.columns) ? ui.columns : [];
}

/**
 * Return a matched content block from an array of TEI blocks, resolving the
 * configured TEI language and block type (input-first) priority ladder.
 * Passes each matched block's `content` and `placeholder_text` through
 * extractTextFromHtml() only — skipping extractHtml() — so that inline marks
 * like <strong> and <em> are stripped but their text content is preserved.
 * (extractHtml only allows br/div/span; passing content through it would
 * discard text wrapped in bold/italic marks and return an empty string.)
 * The filter ladder (input → text, teiLang → TEI_DEFAULT_LANGUAGE) is
 * intentional and must not be changed — T-Chart, Venn, BubbleMap, and Table
 * GO TEIs all depend on the input-first priority behaviour.
 * @param {Array} blocks - Array of content blocks from the TEI
 * @param {string} teiLang - The configured TEI language code
 * @returns {Object|undefined}
 */
export function getContentBlock(blocks, teiLang) {
  let block;

  /*
    Try to match and input first. If not found, then try to match text content.
    Do this for the configured TEI language, and then fall back on the default.
    Return the first match found.
  */
  const tryFilters = [
    (b) => b.type === 'input' && b.lang === teiLang && b.id,
    (b) => b.type === 'text' && b.lang === teiLang && b.id,
    (b) => b.type === 'input' && (b.lang === TEI_DEFAULT_LANGUAGE || !b.lang) && b.id,
    (b) => b.type === 'text' && (b.lang === TEI_DEFAULT_LANGUAGE || !b.lang) && b.id,
  ];

  for (let funcIndex = 0; funcIndex < tryFilters.length; funcIndex += 1) {
    const filterFunc = tryFilters[funcIndex];

    /*
      Try each filter in priority order and use the first
      block that matches.
    */
    block = chain(blocks)
      .filter(filterFunc)
      .map((b) => ({
        ...b,
        /*
          Only allow plain text from these fields. If there is rich text,
          then extract allowed elements and then extract text content from that.
          `placeholder_text` may be a legacy flat string OR the new lang-array
          shape [{lang, content}]; resolve via resolveLangArrayField first.
        */
        placeholder_text: extractTextFromHtml(resolveLangArrayField(b.placeholder_text, teiLang)),
        content: extractTextFromHtml(b.content),
      }))
      .first()
      .value();

    if (block) {
      break;
    }
  }

  return block;
}
